import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, GraduationCap, Users, BookOpen } from 'lucide-react'
import Button from '../ui/Button'

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [resetTimer, setResetTimer] = useState(0)

  const slides = [
    {
      id: 1,
      title: "Las Gardenias Institución Educativa Distrital ",
      subtitle: "Sujetos constructores de vida, esperanza y resiliencia.",
      description: "Institución educativa oficial que forma ciudadanos íntegros con competencias académicas, sociales y emocionales para transformar positivamente su entorno y contribuir al desarrollo de la sociedad.",
      cta: "Conoce más",
      ctaLink: "/nosotros",
      secondaryCta: "Ver Programas",
      secondaryCtaLink: "/programas",
      gradient: "from-black/20 to-black/40",
      icon: GraduationCap,
      iconImage: "/images/IconFlor.png",
      image: "/images/slide1.jpg"
    },
    {
      id: 2,
      title: "2,200 Estudiantes Confiaron en Nosotros",
      subtitle: "Excelencia Académica",
      description: "Con un equipo de 70 profesionales comprometidos con la educación integral",
      cta: "Ver Programas",
      ctaLink: "/programas",
      secondaryCta: "Admisiones 2026",
      secondaryCtaLink: "/admisiones",
      gradient: "from-transparent via-black/20 to-black/50",
      icon: Users,
      iconImage: "/images/IconFuego.png",
      image: "/images/slide2.jpg"
    },
    {
      id: 3,
      title: "Admisiones Abiertas 2026",
      subtitle: "¡Tu Futuro Comienza Aquí!",
      description: "Proceso de admisión abierto para Transición, Básica Primaria y Bachillerato",
      cta: "Inscríbete Ya",
      ctaLink: "/admisiones",
      secondaryCta: "Más Información",
      secondaryCtaLink: "/contacto",
      gradient: "from-gray-800/30 to-gray-900/30",
      icon: BookOpen,
      iconImage: "/images/IconBuho.png",
      image: "/images/slide3.jpg"
    },
    {
      id: 4,
      title: "Las Gardenias Institución Educativa Distrital ",
      subtitle: "Paz, Educación y Progreso",
      description: "Formando líderes integrales desde 2017 con educación de calidad en Barranquilla",
      cta: "Nuestra Historia",
      ctaLink: "/nosotros",
      secondaryCta: "Contáctanos",
      secondaryCtaLink: "/contacto",
      gradient: "from-black/20 to-black/40",
      icon: GraduationCap,
      iconImage: "/images/IconLibro.png",
      image: "/images/slide4.jpg"
    }
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) =>
        prevSlide === slides.length - 1 ? 0 : prevSlide + 1
      )
    }, 6000)

    return () => clearInterval(timer)
  }, [slides.length, resetTimer])

  const goToSlide = (index) => {
    setCurrentSlide(index)
    setResetTimer(prev => prev + 1)
  }

  const goToPrevious = () => {
    setCurrentSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1)
    setResetTimer(prev => prev + 1)
  }

  const goToNext = () => {
    setCurrentSlide(currentSlide === slides.length - 1 ? 0 : currentSlide + 1)
    setResetTimer(prev => prev + 1)
  }

  // Obtener el slide actual
  const currentSlideData = slides[currentSlide]
  const IconComponent = currentSlideData.icon

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchpriority={index === 0 ? 'high' : 'low'}
          />
          <div className={`absolute inset-0 bg-gradient-to-b ${slide.gradient}`}></div>
        </div>
      ))}

      <div className="absolute inset-0 bg-black/10"></div>

      {/* Slide Content - flex-1 + min-h-0: ocupa el espacio que sobra arriba de los
          controles y se centra ahí, así el texto nunca empuja los controles fuera
          de flujo ni se solapa con ellos, sin importar cuántas líneas ocupe. */}
      <div className="relative z-10 flex-1 flex-shrink-0 flex items-center justify-center container-custom px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 md:pt-20 lg:pt-24">
        <div className="text-center max-w-full mx-auto">
          {/* Icon */}
          <div className="mb-4 sm:mb-6 md:mb-8 flex justify-center">
            <div className={`inline-flex items-center justify-center rounded-full bg-gradient-to-r ${currentSlideData.gradient} shadow-2xl w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20`}>
              <img
                src={currentSlideData.iconImage}
                alt={currentSlideData.title}
                className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-2 sm:mb-3 md:mb-4 animate-fade-in-up text-shadow-lg px-4">
            {currentSlideData.title}
          </h1>

          {/* Subtitle */}
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gardenia-gold mb-3 sm:mb-4 md:mb-6 animate-fade-in-up text-shadow px-4">
            {currentSlideData.subtitle}
          </h2>

          {/* Description */}
          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-4 sm:mb-6 md:mb-8 px-4 sm:px-8 md:px-12 leading-relaxed animate-fade-in-up text-shadow max-w-4xl mx-auto">
            {currentSlideData.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-fade-in-up px-4">
            <Button
              to={currentSlideData.ctaLink}
              size="md"
              className="w-full sm:w-auto min-w-[200px]"
            >
              {currentSlideData.cta}
            </Button>
            <Button
              to={currentSlideData.secondaryCtaLink}
              variant="outline-white"
              size="md"
              className="w-full sm:w-auto min-w-[200px]"
            >
              {currentSlideData.secondaryCta}
            </Button>
          </div>
        </div>
      </div>

      {/* Contenedor de controles - en flujo normal, siempre debajo del contenido,
          nunca se solapa con él aunque el título ocupe varias líneas. */}
      <div className="relative z-30 flex-shrink-0 pb-6 sm:pb-10">
        <div className="flex items-center justify-center gap-4">
          {/* Flecha izquierda */}
          <button
            onClick={goToPrevious}
            className="group"
            aria-label="Slide anterior"
          >
            <div className="relative">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-300 group-hover:bg-white/30 group-hover:scale-110 group-hover:border-white/50 shadow-2xl">
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-white group-hover:text-gardenia-gold transition-colors duration-300" />
              </div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gardenia-red/20 to-gardenia-green/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
            </div>
          </button>

          {/* Indicadores de slide (centrados) */}
          <div className="relative">
            <div className="flex space-x-2 sm:space-x-3 bg-white/10 backdrop-blur-lg rounded-2xl px-4 sm:px-6 py-2 sm:py-3 border border-white/20 shadow-2xl">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`relative group transition-all duration-500 ${
                    index === currentSlide ? 'scale-125' : ''
                  }`}
                  aria-label={`Ir al slide ${index + 1}`}
                >
                  <div
                    className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all duration-500 ${
                      index === currentSlide
                        ? 'bg-gardenia-gold shadow-lg'
                        : 'bg-white/40 group-hover:bg-white/70'
                    }`}
                  ></div>
                  {index === currentSlide && (
                    <div className="absolute inset-0 rounded-full bg-gardenia-gold animate-ping opacity-30"></div>
                  )}
                  <div className="absolute -inset-2 rounded-full bg-gradient-to-r from-gardenia-red/20 to-gardenia-green/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                </button>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-gardenia-red/10 via-gardenia-gold/10 to-gardenia-green/10 rounded-2xl blur-xl opacity-50"></div>
          </div>

          {/* Flecha derecha */}
          <button
            onClick={goToNext}
            className="group"
            aria-label="Slide siguiente"
          >
            <div className="relative">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center transition-all duration-300 group-hover:bg-white/30 group-hover:scale-110 group-hover:border-white/50 shadow-2xl">
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-white group-hover:text-gardenia-gold transition-colors duration-300" />
              </div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gardenia-green/20 to-gardenia-red/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
            </div>
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20 hidden md:block">
        <div className="flex flex-col items-center text-white/70 animate-bounce-soft">
          <span className="text-xs mb-1 font-normal">Desliza hacia abajo</span>
          <div className="w-px h-4 bg-gradient-to-b from-white/70 to-transparent"></div>
        </div>
      </div>
    </section>
  )
}

export default HeroSlider